/**
 * Pyrana brand theme for Backstage.
 *
 * Palette + typography captured live from https://pyrana.ai (see LogoFull.tsx
 * for the matching sidebar mark). The marketing brand is warm: a burnt-orange
 * accent on a paper background, Geist for UI + Geist Mono for code.
 *
 * Registered via ThemeBlueprint in App.tsx (NOT passed raw to createApp
 * features — that path silently fails to register in the new frontend system).
 */
import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

// --- Brand tokens (verified from live site) ---------------------------------
const brand = {
  orange: '#E4734B', // primary accent / CTA
  orangeDark: '#C4572E', // gradient end / hover on light
  orangeLight: '#ED8B6A',
  paper: '#FCF9F8', // warm off-white page background
  surface: '#FFFFFF',
  ink: '#1D1B1B', // primary text (warm near-black)
  inkSubtle: '#57433D', // secondary text (warm taupe)
  muted: '#999999',
  dark: '#1A1A1A', // dark surfaces / sidebar
  darkPaper: '#242424',
};

const fontFamily =
  "'Geist Variable', 'Geist', 'Helvetica Neue', Helvetica, Roboto, Arial, sans-serif";

// Shared orange gradient for entity/page headers.
const orangeGradient = genPageTheme({
  colors: [brand.orange, brand.orangeDark],
  shape: shapes.wave,
});

const pageTheme = {
  home: orangeGradient,
  documentation: orangeGradient,
  tool: orangeGradient,
  service: orangeGradient,
  website: orangeGradient,
  library: orangeGradient,
  other: orangeGradient,
  app: orangeGradient,
  apis: orangeGradient,
};

export const pyranaLightTheme = createUnifiedTheme({
  fontFamily,
  defaultPageTheme: 'home',
  pageTheme,
  palette: {
    ...palettes.light,
    primary: { main: brand.orange, dark: brand.orangeDark, light: brand.orangeLight },
    secondary: { main: brand.ink, dark: '#000000', light: brand.inkSubtle },
    background: { default: brand.paper, paper: brand.surface },
    text: { primary: brand.ink, secondary: brand.inkSubtle },
    link: brand.orangeDark,
    linkHover: brand.orange,
    navigation: {
      ...palettes.light.navigation,
      background: brand.dark,
      indicator: brand.orange,
      color: '#B5AEAB',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: 'rgba(228, 115, 75, 0.14)' },
      submenu: { background: brand.darkPaper },
    },
  },
});

export const pyranaDarkTheme = createUnifiedTheme({
  fontFamily,
  defaultPageTheme: 'home',
  pageTheme,
  palette: {
    ...palettes.dark,
    primary: { main: brand.orange, dark: brand.orangeDark, light: brand.orangeLight },
    secondary: { main: brand.orangeLight },
    background: { default: brand.dark, paper: brand.darkPaper },
    text: { primary: '#F5F1EF', secondary: '#B5AEAB' },
    link: brand.orangeLight,
    linkHover: brand.orange,
    navigation: {
      ...palettes.dark.navigation,
      background: '#141414',
      indicator: brand.orange,
      color: '#B5AEAB',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: 'rgba(228, 115, 75, 0.16)' },
      submenu: { background: '#1F1F1F' },
    },
  },
});
